import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Instagram, Linkedin } from 'lucide-react';
import OptimizedImage from '@/components/OptimizedImage';
import GrainTexture from '@/components/GrainTexture';
import siteLogo from '@/assets/organisatieopstellingen-logo.png';
import siteLogoWebp from '@/assets/organisatieopstellingen-logo.webp';
import { SITE_NAME } from '@/seo/siteConfig';

const NAV_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Aanbod', path: '/aanbod' },
  { label: 'Agenda', path: '/agenda' },
  { label: 'Over', path: '/over' },
  { label: 'Contact', path: '/contact' },
  { label: 'Summer Read', path: '/summer-read' },
] as const;

const SERVICE_LINKS = [
  { label: 'Organisatieopstellingen', path: '/organisatieopstellingen' },
  { label: 'Teamcoaching', path: '/teamcoaching' },
  { label: 'Opleidingstrajecten', path: '/opleidingstrajecten' },
  { label: 'Leergang systemisch leiderschap', path: '/leergang-onderstroom' },
  { label: '1-op-1 coaching', path: '/1-op-1' },
] as const;

const Footer: React.FC = () => {
  const navigate = useNavigate();

  const handleNav = (path: string) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#854C73] text-white pt-20 pb-10 relative overflow-hidden">
      <GrainTexture opacity={0.15} />
      <div className="container mx-auto px-6 relative z-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          <div className="lg:col-span-1">
            <OptimizedImage
              src={siteLogo}
              webpSrc={siteLogoWebp}
              alt={SITE_NAME}
              className="h-10 w-auto mb-6 brightness-0 invert"
              width={120}
              height={40}
            />
            <p className="text-white/80 text-lg leading-relaxed max-w-md mb-8 font-light">
              Systemisch werk voor organisaties en teams. Organisatieopstellingen, teamcoaching en systemische leergang voor leiders vanuit Castricum, in-house of op locatie.
            </p>
          </div>

          <div>
            <h4 className="font-bold mb-6 uppercase tracking-widest text-sm opacity-60">Navigatie</h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((item) => (
                <li key={item.path}>
                  <button
                    onClick={() => handleNav(item.path)}
                    className="text-white/80 hover:text-white transition-colors hover:translate-x-1 inline-block duration-300"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-bold mb-6 uppercase tracking-widest text-sm opacity-60">Diensten</h4>
            <ul className="space-y-3">
              {SERVICE_LINKS.map((item) => (
                <li key={item.path}>
                  <button
                    onClick={() => handleNav(item.path)}
                    className="text-white/80 hover:text-white transition-colors hover:translate-x-1 inline-block duration-300"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-white/60 leading-relaxed">
              Familieopstellingen en strippenkaart:{' '}
              <a
                href="https://www.systemique.nl/agenda"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/80 underline underline-offset-4 hover:text-white"
              >
                systemique.nl
              </a>
            </p>
          </div>

          <div>
            <h4 className="font-bold mb-6 uppercase tracking-widest text-sm opacity-60">Contact</h4>
            <ul className="space-y-3 text-white/80">
              <li className="flex items-center gap-3">
                <Mail size={18} aria-hidden="true" />
                <a href="mailto:marthe@systemique.nl" className="hover:text-white transition-colors">
                  marthe@systemique.nl
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} aria-hidden="true" />
                <a href="tel:+31612991453" className="hover:text-white transition-colors">
                  +31 6 1299 1453
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MapPin size={18} aria-hidden="true" />
                <span>Castricum & Regio</span>
              </li>
              <li className="flex items-center gap-3">
                <Instagram size={18} aria-hidden="true" />
                <a
                  href="https://www.instagram.com/systemique.nl/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                >
                  @systemique.nl
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Linkedin size={18} aria-hidden="true" />
                <a
                  href="https://www.linkedin.com/in/marthe-van-der-molen"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                >
                  LinkedIn
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 pt-8 text-center text-sm text-white/60">
          <p>© {new Date().getFullYear()} Organisatieopstellingen. Alle rechten voorbehouden.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
